import * as next from '../../../types/next'
import { SINGLE_CHOICE_UI_TYPE } from '../../../types/next'
import { getBlockNameByScope } from '../../../utils'
import type {
  EnumControlProps,
  EnumButtonProps,
  EnumImageButtonProps
} from '../../../utils'
import { CONTROL_NAME } from '../../../utils/blocks/types'
import type { UpBlockHandler } from '../types'
import { newId } from '../utils'

import { up as upBinaryInput } from './binaryControl'
import { nonEmptyStringOrUndefined, upCommonSettings } from './common'
import { up as upTextInput } from './textInputControl'

export const up: UpBlockHandler<
  next.SingleChoiceBlock | next.BinaryBlock | next.TextInputBlock
> = (uischema, schema, journey) => {
  if (uischema.type !== CONTROL_NAME.CONTROL) {
    throw new Error(
      'Expected a valid Single Choice, Binary or Text Input control'
    )
  }

  const name = String(
    getBlockNameByScope(uischema.scope) ?? uischema.options?.title
  )

  // If the control is a binary input, we need to transform it to a binary block
  const schemaType = schema?.properties?.[name]?.type

  if (schemaType === undefined) {
    throw new Error(`Expeceted a valid schema type for: ${name}`)
  }

  if (schemaType === 'boolean') {
    return upBinaryInput(uischema, schema, journey)
  }

  if (schemaType === 'string' && !schema?.properties?.[name]?.enum) {
    return upTextInput(uischema, schema, journey)
  }

  const castedOptions = (uischema.options || {}) as any
  const uiType = getBlockUiType(castedOptions)
  const choices = upChoices(castedOptions, uiType)

  switch (uiType) {
    case SINGLE_CHOICE_UI_TYPE.BUTTON: {
      return {
        type: next.BLOCK_TYPE.SINGLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          label: nonEmptyStringOrUndefined(castedOptions.label),
          uiType,
          buttonType: upLayoutType((castedOptions as any).buttonType),
          choices
        },
        references: {}
      }
    }

    case SINGLE_CHOICE_UI_TYPE.RADIO: {
      return {
        type: next.BLOCK_TYPE.SINGLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          label: nonEmptyStringOrUndefined(castedOptions.label),
          uiType,
          choices
        },
        references: {}
      }
    }

    case SINGLE_CHOICE_UI_TYPE.DROPDOWN: {
      const choicesFileId = (castedOptions as any).buttonOptionsFileId

      return {
        type: next.BLOCK_TYPE.SINGLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          label: nonEmptyStringOrUndefined(castedOptions.label),
          uiType,
          choices: choicesFileId ? [] : choices,
          choicesFileId
        },
        references: {}
      }
    }

    case SINGLE_CHOICE_UI_TYPE.IMAGE: {
      return {
        type: next.BLOCK_TYPE.SINGLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          label: nonEmptyStringOrUndefined(castedOptions.label),
          uiType,
          imageType: upImageType((castedOptions as any).imageType),
          choices
        },
        references: {}
      }
    }

    default:
      throw new Error(`Unsupported UI type: ${uiType}`)
  }
}

const getBlockUiType = (options: next.UncastObject) => {
  if ('button' in options) return SINGLE_CHOICE_UI_TYPE.BUTTON
  if ('imageButton' in options) return SINGLE_CHOICE_UI_TYPE.IMAGE
  if ('radio' in options) return SINGLE_CHOICE_UI_TYPE.RADIO

  return SINGLE_CHOICE_UI_TYPE.DROPDOWN
}

const upLayoutType = (
  buttonType: EnumButtonProps['buttonType']
): next.BUTTON_CHOICE_LAYOUT_TYPE => {
  switch (buttonType) {
    case 'row':
      return next.BUTTON_CHOICE_LAYOUT_TYPE.ROW

    case 'stack':
      return next.BUTTON_CHOICE_LAYOUT_TYPE.STACK

    case 'chip':
    default:
      return next.BUTTON_CHOICE_LAYOUT_TYPE.CHIP
  }
}

const upImageType = (imageType: any): next.IMAGE_CHOICE_UI_SUBTYPE => {
  switch (imageType) {
    case 'icon':
      return next.IMAGE_CHOICE_UI_SUBTYPE.ICON
    case 'infographic':
    case undefined:
    case null:
      return next.IMAGE_CHOICE_UI_SUBTYPE.INFOGRAPHIC
    default:
      throw new Error(`Unsupported image type: ${imageType}`)
  }
}

const upChoices = <T extends next.AnyChoice>(
  uischemaOptions: EnumControlProps | EnumButtonProps | EnumImageButtonProps,
  uiType: next.SINGLE_CHOICE_UI_TYPE
) => {
  const castedOptions = uischemaOptions as any

  return (uischemaOptions.options ?? []).reduce<T[]>((acc, choice, i) => {
    const label = castedOptions.optionsLabels?.[i]
    const icon = castedOptions.optionsIcons?.[i]
    const image = castedOptions.optionsImages?.[i]
    const alt = castedOptions.optionsImageDescriptions?.[i]

    acc.push({
      value: choice,
      ...(typeof label !== 'undefined' && { label }),
      ...(typeof icon !== 'undefined' && { icon }),
      ...(uiType === next.SINGLE_CHOICE_UI_TYPE.IMAGE &&
        image && {
          image: {
            id: image.id ?? newId<next.ImageId>(),
            purpose: alt
              ? next.IMAGE_PURPOSE.INFORMATIVE
              : next.IMAGE_PURPOSE.DECORATIVE,
            url: image.url,
            ...(image.dimension && { dimensions: image.dimension }),
            ...(alt && { alt })
          }
        })
    })

    return acc
  }, []) as T[]
}
